
import {
  SlideXLeftTransition,
  FadeTransition,
  SlideYDownTransition,
} from 'vue2-transitions'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'ResourceDirectory',

  components: {
    SlideXLeftTransition,
    FadeTransition,
    SlideYDownTransition,
    SideNavigation: () => import('@/components/PageElements/SideNavigation'),
  },

  props: {
    index: {
      type: Number,
      default: 2,
    },
  },

  async asyncData({ $api, $bugsnag, query }) {
    const data = {
      categories: [],
      specifics: {},
      isResourcesModalOpen: false,
    }

    try {
      data.categories = await $api.resources
        .getCategories()
        .then((res) => res.data.results)

      // if navigating to specific resource, load and show that
      if (query && query.resourceId) {
        data.specifics = await $api.resources
          .getResourcesByID(query.resourceId)
          .then((res) => res.data)

        data.isResourcesModalOpen = true
      }
    } catch (error) {
      $bugsnag.notify(error)
    }

    return {
      categories: data.categories,
      isResourcesModalOpen: data.isResourcesModalOpen,
      specifics: data.specifics,
    }
  },

  data() {
    return {
      isResourcesLoading: false,
      resource: {
        id: 554565,
        title: 'Resource Directory',
        databaseID: 2222,
        modified: '11/02/2020',
        description:
          'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation u',
      },

      crumbs: [
        {
          label: 'resources',
          to: '/resources',
        },
      ],

      pages: {
        cursor: 1,
        nbPages: 1,
        nbHits: 1,
        limit: 10,
      },

      resources: [],
      searchKeyword: '',
      selectedCategory: null,
      categories: [],

      isSelectedCountyOrZip: false,
      isSelectedCategoryOrKeyword: false,

      showMobileSearchWithinResults: false,
      searchWithinKeyword: '',
    }
  },

  computed: {
    ...mapState('resources', ['zip', 'county']),

    isResponsiveResourceModalOpen() {
      return (
        ['sm', 'md', 'lg', 'xl', 'xxl'].includes(this.$mq) &&
        this.isResourcesModalOpen
      )
    },

    counties() {
      return this.$store.getters['general/county']
    },
  },

  watch: {
    searchKeyword() {
      if (this.zip || this.county) {
        this.isSelectedCategoryOrKeyword = true
        this.getResources({
          searchWord: this.searchKeyword,
          county: this.county,
          zip: this.zip,
          catId: this.selectedCategory && this.selectedCategory.categoryId,
        })
      }
    },

    '$store.state.resources.county'(value) {
      if (!value) return
      this.setZip('')

      if (this.searchKeyword || this.selectedCategory) {
        this.getResources({
          searchWord: this.searchKeyword,
          county: this.county,
          zip: this.zip,
          catId: this.selectedCategory && this.selectedCategory.categoryId,
        })
      } else {
        this.isSelectedCountyOrZip = true
      }
    },

    '$store.state.resources.zip'(value) {
      if (value.length < 5) return
      this.setCounty('')
      if (this.searchKeyword || this.selectedCategory) {
        this.getResources({
          searchWord: this.searchKeyword,
          county: this.county,
          zip: this.zip,
          catId: this.selectedCategory && this.selectedCategory.categoryId,
        })
      } else {
        this.isSelectedCountyOrZip = true
      }
    },

    selectedCategory: {
      deep: true,
      handler(value) {
        if (this.zip || this.county) {
          this.isSelectedCountyOrZip = true

          if (value === null) {
            this.resources = []
          } else {
            this.getResources({
              searchWord: this.searchKeyword,
              county: this.county,
              zip: this.zip,
              catId: this.selectedCategory && this.selectedCategory.categoryId,
            })
          }
        }
      },
    },
  },

  async created() {
    const { searchWord = '' } = this.$route.query
    if (this.county || this.zip) {
      this.isSelectedCountyOrZip = true
    }
    await this.getResources({
      zip: this.zip,
      county: this.county,
      searchWord,
    })
  },

  methods: {
    ...mapActions('resources', ['setZip', 'setCounty']),

    focusList() {
      document.getElementById('resource-list').focus()
    },

    onResourceSelection(resource) {
      this.specifics = resource
      this.isResourcesModalOpen = !this.isResourcesModalOpen

      history.replaceState({}, null, `/resources?resourceId=${resource.id}`)

      this.$nextTick(() => {
        if (this.$refs.mobileResoureModal) {
          this.$refs.mobileResoureModal.$el
            .querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            .focus()
        } else if (this.$refs.desktopResourcePanel) {
          this.$refs.desktopResourcePanel.$el
            .querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            .focus()
        }
      })
    },

    onSearchWithinClick() {
      this.showMobileSearchWithinResults = true
      this.$nextTick(() => {
        this.$refs.searchWithinInput.$el.querySelector('input').focus()
      })
    },

    onFilterClear() {
      this.setZip('')
      this.setCounty('')
      this.searchKeyword = ''
      this.selectedCategory = ''
      this.isSelectedCountyOrZip = false
      this.isSelectedCategoryOrKeyword = false
      this.resources = []

      this.getResources()
    },

    onCategorySelection(category) {
      this.searchKeyword = ''
      this.selectedCategory = category
    },

    onResourceModalClose() {
      this.isResourcesModalOpen = false
      this.specifics = {}
      document.getElementById('resource-list').focus()
      history.replaceState({}, null, `/resources`)
    },

    onPaginate(page) {
      this.$scrollTo('#resources-content')

      this.getResources({
        searchWord: this.searchKeyword,
        county: this.county,
        zip: this.zip,
        catId: this.selectedCategory && this.selectedCategory.categoryId,
        page,
      })
    },

    async getResources({
      searchWord = this.searchKeyword,
      zip = this.zip,
      county = this.county,
      catId = this.selectedCategory && this.selectedCategory.categoryId,
      page,
    } = {}) {
      const params = {}
      if (searchWord) {
        params.keyword = searchWord
      }
      if (zip) {
        params.zipPostalCode = zip
      }
      if (county) {
        params.county = county.trim()
      }
      if (catId) {
        params.sub_set = catId
      }
      if (page) {
        params.cursor = page - 1
      } else {
        params.cursor = 0
      }
      params.limit = this.pages.limit

      if (searchWord || catId) {
        try {
          this.isResourcesLoading = true
          await this.$api.resources.getResourcesByCat(params).then((r) => {
            this.pages.cursor = r.data.cursor
            this.pages.nbPages = r.data.nbPages
            this.pages.nbHits = r.data.nbHits
            this.pages.limit = r.data.limit
            this.resources = r.data.results
          })
        } catch (error) {
          this.$bugsnag.notify(error)
        } finally {
          this.isResourcesLoading = false
        }
      }
    },
  },

  head() {
    return {
      title: 'Resources',
      meta: this.$helpers.addMetaTags({
        title: 'Resources for Victims of Crime',
        description:
          'Find resources for victims of crime, including eduction, support, and information on how to get help.',
      }),
    }
  },
}
